import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, ArrowRight, CheckCircle2, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { newsletterService } from '../services/newsletterService';
import { cn } from '../lib/utils';
import { useTheme } from '../context/ThemeContext';

export default function Footer() {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    try {
      setSubmitting(true);
      setError(null);
      const response = await newsletterService.subscribe(email, 'footer');
      if (response.success) {
        setSubscribed(true);
        setEmail('');
      } else {
        setError(response.message || 'Subscription failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className={cn(
      "pt-16 pb-12 relative overflow-hidden transition-colors duration-300 w-full",
      isLight
        ? "bg-[#F0F6FF] border-t border-slate-200 text-slate-900"
        : "bg-[#030708] border-t border-white/[0.06] text-white"
    )}>
      {/* Background glow on dark mode */}
      {!isLight && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[550px] h-[400px] bg-brand-500/5 rounded-full blur-[140px] pointer-events-none" />
      )}

      <div className="w-full max-w-[115rem] mx-auto px-6 sm:px-10 lg:px-14 xl:px-16 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 xl:gap-12 mb-16">
          {/* Brand */}
          <div className="space-y-5 sm:col-span-2 lg:col-span-3 pr-2">
            <Link to="/" className="inline-block transition-transform hover:scale-105">
              <img
                src={isLight ? "/assets/images/logo.webp?v=1.2" : "/assets/images/white-logo.png"}
                alt="Prince IT Solution"
                className="h-9 sm:h-10 w-auto object-contain"
                referrerPolicy="no-referrer"
                loading="lazy"
              />
            </Link>
            <p className={cn(
              "text-sm leading-relaxed max-w-sm",
              isLight ? "text-slate-800 font-medium" : "text-slate-400 font-normal"
            )}>
              Design, Build and Grow with a Trusted IT Partner. We deliver tailored technology solutions to empower your business globally.
            </p>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h3 className={cn(
              "font-display font-extrabold text-base tracking-wide mb-5",
              isLight ? "text-slate-950" : "text-white"
            )}>
              Services
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Web Development', path: '/services/web-development' },
                { name: 'Mobile App Development', path: '/services/mobile-app' },
                { name: 'UI/UX Design', path: '/services/uiux-design' },
                { name: 'Digital Marketing', path: '/services/digital-marketing' },
                { name: 'Cloud & DevOps', path: '/services/cloud-devops' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={cn(
                      "text-sm transition-colors duration-200 inline-block",
                      isLight
                        ? "text-slate-800 font-semibold hover:text-brand-600 hover:translate-x-0.5"
                        : "text-slate-400 hover:text-white font-normal"
                    )}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h3 className={cn(
              "font-display font-extrabold text-base tracking-wide mb-5",
              isLight ? "text-slate-950" : "text-white"
            )}>
              Company
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'About Us', path: '/about-us' },
                { name: 'Portfolio', path: '/portfolio' },
                { name: 'Technology', path: '/technology' },
                { name: 'Blog & Insights', path: '/blog' },
                { name: 'Contact', path: '/contact-us' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className={cn(
                      "text-sm transition-colors duration-200 inline-block",
                      isLight
                        ? "text-slate-800 font-semibold hover:text-brand-600 hover:translate-x-0.5"
                        : "text-slate-400 hover:text-white font-normal"
                    )}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Get in Touch */}
          <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
            <h3 className={cn(
              "font-display font-extrabold text-base tracking-wide mb-5",
              isLight ? "text-slate-950" : "text-white"
            )}>
              Get in Touch
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className={cn(
                  "w-4 h-4 shrink-0 mt-1",
                  isLight ? "text-brand-600" : "text-brand-400"
                )} />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Prince+IT+Solution+Silver+Trade+Center+Mota+Varachha+Surat"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(
                    "text-sm leading-relaxed transition-colors",
                    isLight
                      ? "text-slate-800 font-semibold hover:text-brand-600"
                      : "text-slate-400 hover:text-white font-normal"
                  )}
                >
                  Prince IT Solution, 504, Silver Trade Center, Beside Oxygen Park, Mota Varachha, Surat Gujarat 394105
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone className={cn(
                  "w-4 h-4 shrink-0",
                  isLight ? "text-brand-600" : "text-brand-400"
                )} />
                <a
                  href="tel:+919023796107"
                  className={cn(
                    "text-sm transition-colors",
                    isLight
                      ? "text-slate-800 font-semibold hover:text-brand-600"
                      : "text-slate-400 hover:text-white font-normal"
                  )}
                >
                  +91 90237 96107
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className={cn(
                  "w-4 h-4 shrink-0",
                  isLight ? "text-brand-600" : "text-brand-400"
                )} />
                <a
                  href="mailto:sales@princeitsolution.com"
                  className={cn(
                    "text-sm transition-colors",
                    isLight
                      ? "text-slate-800 font-semibold hover:text-brand-600"
                      : "text-slate-400 hover:text-white font-normal"
                  )}
                >
                  sales@princeitsolution.com
                </a>
              </li>
            </ul>
          </div>

          {/* Stay Updated */}
          <div className="sm:col-span-2 lg:col-span-3 xl:col-span-3">
            <h3 className={cn(
              "font-display font-extrabold text-base tracking-wide mb-5",
              isLight ? "text-slate-950" : "text-white"
            )}>
              Stay Updated
            </h3>
            <p className={cn(
              "text-sm mb-4",
              isLight ? "text-slate-800 font-medium" : "text-slate-400 font-normal"
            )}>
              Get the latest insights delivered to your inbox.
            </p>

            {subscribed ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn("flex items-center gap-2", isLight ? "text-emerald-600 font-bold" : "text-brand-400")}
              >
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-bold">Successfully subscribed!</span>
              </motion.div>
            ) : (
              <form onSubmit={handleSubscribe} className="space-y-3 max-w-sm">
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email address"
                    required
                    disabled={submitting}
                    className={cn(
                      "w-full rounded-xl py-3 px-4 text-sm transition-all disabled:opacity-50",
                      isLight
                        ? "bg-white border-2 border-slate-300 text-slate-900 font-medium placeholder:text-slate-500 focus:outline-none focus:border-brand-600 shadow-sm"
                        : "bg-[#111616]/90 border border-white/[0.08] text-white placeholder:text-slate-500 focus:outline-none focus:border-brand-500/50"
                    )}
                  />
                </div>
                {error && (
                  <p className="text-red-500 text-xs px-1 font-semibold">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className={cn(
                    "w-full py-3 px-5 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer shadow-md",
                    isLight
                      ? "bg-brand-600 hover:bg-brand-500 text-white shadow-brand-500/20"
                      : "bg-brand-500 hover:bg-brand-400 text-slate-950 shadow-[0_0_20px_rgba(var(--app-primary-rgb),0.3)] hover:shadow-[0_0_25px_rgba(var(--app-primary-rgb),0.5)]"
                  )}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <span>Subscribe</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={cn(
          "pt-8 flex flex-col md:flex-row justify-between items-center gap-4",
          isLight ? "border-t border-slate-300" : "border-t border-white/[0.08]"
        )}>
          <p className={cn(
            "text-xs sm:text-sm font-medium",
            isLight ? "text-slate-700 font-semibold" : "text-slate-400"
          )}>
            © {new Date().getFullYear()} Prince IT Solution. All rights reserved.
          </p>
          <div className="flex gap-2.5 sm:gap-3 items-center">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/PrinceITSolutionIndia"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 group hover:scale-110 hover:-translate-y-0.5",
                isLight
                  ? "bg-white border-slate-300 text-slate-800 hover:bg-[#1877F2] hover:border-[#1877F2] hover:text-white shadow-sm"
                  : "bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/10 hover:border-white/20"
              )}
              aria-label="Facebook"
            >
              <svg className="w-4 h-4 fill-current transition-colors" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </a>

            {/* Twitter / X */}
            <a
              href="https://x.com/Prince_IT_Sol"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 group hover:scale-110 hover:-translate-y-0.5",
                isLight
                  ? "bg-white border-slate-300 text-slate-800 hover:bg-[#1DA1F2] hover:border-[#1DA1F2] hover:text-white shadow-sm"
                  : "bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/10 hover:border-white/20"
              )}
              aria-label="Twitter"
            >
              <svg className="w-3.5 h-3.5 fill-current transition-colors" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/princeit_solution"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 group hover:scale-110 hover:-translate-y-0.5",
                isLight
                  ? "bg-white border-slate-300 text-slate-800 hover:bg-[#E4405F] hover:border-[#E4405F] hover:text-white shadow-sm"
                  : "bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-[#E4405F] hover:border-[#E4405F]"
              )}
              aria-label="Instagram"
            >
              <svg className="w-4 h-4 fill-current transition-colors" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>

            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/company/princeitsolution"
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                "w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 group hover:scale-110 hover:-translate-y-0.5",
                isLight
                  ? "bg-white border-slate-300 text-slate-800 hover:bg-[#0A66C2] hover:border-[#0A66C2] hover:text-white shadow-sm"
                  : "bg-white/[0.04] border-white/[0.08] text-slate-400 hover:text-white hover:bg-white/10 hover:border-white/20"
              )}
              aria-label="LinkedIn"
            >
              <svg className="w-4 h-4 fill-current transition-colors" viewBox="0 0 24 24">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9v8.37H9.25V10.9H6.46M7.86 6.78a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
